/**
 * 本部設定 > 基本情報ページ (/tenant/basic)
 *
 * tenant / tenant_industry / tenant_setting をまとめて表示する。
 */
import { useEffect, useState } from "react";
import clsx from "clsx";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/atoms/Button/Button";
import { TabBar } from "../components/molecules/TabBar";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import {
    useGetTenantBasicQuery,
    useUpdateTenantBasicMutation,
} from "../shared/api/endpoints/tenantApi";
import {
    useGetIndustriesQuery,
    useGetIndustryTypesQuery,
} from "../shared/api/endpoints/industryApi";

const MONTH_OPTIONS: ReadonlyArray<number> = Array.from({ length: 12 }, (_, i) => i + 1);
const DAY_OPTIONS: ReadonlyArray<number> = Array.from({ length: 31 }, (_, i) => i + 1);
const WEEK_DAY_LABELS: ReadonlyArray<string> = ["月", "火", "水", "木", "金", "土", "日"];

interface FormFieldProps {
    label: string;
    required?: boolean;
    error?: string;
    children: React.ReactNode;
}

const FormField = ({ label, required = false, error, children }: FormFieldProps) => (
    <div className="flex items-start gap-6">
        <label className="w-32 text-sm text-gray-700 pt-2 flex-shrink-0">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div className="flex-1">
            {children}
            {error && <div className="mt-1 text-xs text-red-500">{error}</div>}
        </div>
    </div>
);

const TenantBasicPage = () => {
    const menuTitle = useMenuTitle();
    const { data: tenant, isLoading: tenantLoading, error: tenantError } =
        useGetTenantBasicQuery();
    const { data: industriesData, isLoading: industriesLoading } = useGetIndustriesQuery();
    const { data: industryTypesData, isLoading: industryTypesLoading } =
        useGetIndustryTypesQuery();

    const [updateTenantBasic, { isLoading: isSaving }] = useUpdateTenantBasicMutation();
    const [saveMessage, setSaveMessage] = useState<
        { type: "success" | "error"; text: string } | null
    >(null);

    const isLoading = tenantLoading || industriesLoading || industryTypesLoading;

    interface FormState {
        name: string;
        industryId: number | null;
        industryTypeId: number | null;
        fiscalStartMonth: number | null;
        monthStartDay: number | null;
        weekStartDay: number | null;
    }

    const [form, setForm] = useState<FormState>({
        name: "",
        industryId: null,
        industryTypeId: null,
        fiscalStartMonth: null,
        monthStartDay: null,
        weekStartDay: null,
    });

    useEffect(() => {
        if (tenant) {
            setForm({
                name: tenant.name,
                industryId: tenant.industry_id,
                industryTypeId: tenant.industry_type_id,
                fiscalStartMonth: tenant.fiscal_start_month,
                monthStartDay: tenant.month_start_day,
                weekStartDay: tenant.week_start_day,
            });
        }
    }, [tenant]);

    const toNullableNumber = (value: string): number | null =>
        value === "" ? null : Number(value);

    const industries = industriesData?.industries ?? [];
    const industryTypes = (industryTypesData?.industry_types ?? []).filter(
        (it) => form.industryId != null && it.industry_id === form.industryId,
    );

    const REQUIRED_MESSAGE = "必須項目です";
    const errors = {
        name: form.name.trim() === "" ? REQUIRED_MESSAGE : "",
        industryId: form.industryId == null ? REQUIRED_MESSAGE : "",
        fiscalStartMonth: form.fiscalStartMonth == null ? REQUIRED_MESSAGE : "",
        monthStartDay: form.monthStartDay == null ? REQUIRED_MESSAGE : "",
        weekStartDay: form.weekStartDay == null ? REQUIRED_MESSAGE : "",
    };

    const hasError = Object.values(errors).some((msg) => msg !== "");

    const isDirty = tenant
        ? form.name !== tenant.name ||
          form.industryId !== tenant.industry_id ||
          form.industryTypeId !== tenant.industry_type_id ||
          form.fiscalStartMonth !== tenant.fiscal_start_month ||
          form.monthStartDay !== tenant.month_start_day ||
          form.weekStartDay !== tenant.week_start_day
        : false;

    const handleCancel = () => {
        if (!tenant) return;
        setForm({
            name: tenant.name,
            industryId: tenant.industry_id,
            industryTypeId: tenant.industry_type_id,
            fiscalStartMonth: tenant.fiscal_start_month,
            monthStartDay: tenant.month_start_day,
            weekStartDay: tenant.week_start_day,
        });
        setSaveMessage(null);
    };

    const handleSubmit = async () => {
        if (
            hasError ||
            form.industryId == null ||
            form.fiscalStartMonth == null ||
            form.monthStartDay == null ||
            form.weekStartDay == null
        ) {
            return;
        }
        setSaveMessage(null);
        try {
            await updateTenantBasic({
                name: form.name.trim(),
                industry_id: form.industryId,
                industry_type_id: form.industryTypeId,
                fiscal_start_month: form.fiscalStartMonth,
                month_start_day: form.monthStartDay,
                week_start_day: form.weekStartDay,
            }).unwrap();
            setSaveMessage({ type: "success", text: "登録しました" });
        } catch {
            setSaveMessage({
                type: "error",
                text: "更新に失敗しました。時間をおいて再度お試しください。",
            });
        }
    };

    useEffect(() => {
        if (!saveMessage) return;
        const timerId = setTimeout(() => setSaveMessage(null), 3000);
        return () => clearTimeout(timerId);
    }, [saveMessage]);

    return (
        <div className="flex flex-1 flex-col overflow-hidden">
            <PageHeader title={menuTitle} />
            <TabBar parentCode="tenant" />

            <div className="flex-1 overflow-auto px-6 pb-6">
                <div className="bg-white rounded-lg p-6 mb-4">
                    {isLoading ? (
                        <div className="text-sm text-gray-500">読み込み中...</div>
                    ) : tenantError ? (
                        <div className="text-sm text-red-600">
                            基本情報の取得に失敗しました
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="text-xs text-red-500">「*」は登録必須項目です。</div>

                            <FormField label="会社名" required error={errors.name}>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm((prev) => ({ ...prev, name: e.target.value }))
                                    }
                                    className={clsx(
                                        "form-input-field w-80",
                                        errors.name && "border-red-500",
                                    )}
                                />
                            </FormField>

                            <FormField label="業種分類" required error={errors.industryId}>
                                <select
                                    value={form.industryId ?? ""}
                                    onChange={(e) => {
                                        const next = toNullableNumber(e.target.value);
                                        setForm((prev) => ({
                                            ...prev,
                                            industryId: next,
                                            industryTypeId:
                                                next === prev.industryId ? prev.industryTypeId : null,
                                        }));
                                    }}
                                    className={clsx(
                                        "form-select-field w-80",
                                        errors.industryId && "border-red-500",
                                    )}
                                >
                                    <option value="">未設定</option>
                                    {industries.map((industry) => (
                                        <option key={industry.id} value={industry.id}>
                                            {industry.name}
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            <FormField label="業種区分">
                                <select
                                    value={form.industryTypeId ?? ""}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            industryTypeId: toNullableNumber(e.target.value),
                                        }))
                                    }
                                    className="form-select-field w-80"
                                >
                                    <option value="">未設定</option>
                                    {industryTypes.map((industryType) => (
                                        <option key={industryType.id} value={industryType.id}>
                                            {industryType.name}
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            <FormField label="年度の開始月" required error={errors.fiscalStartMonth}>
                                <select
                                    value={form.fiscalStartMonth ?? ""}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            fiscalStartMonth: toNullableNumber(e.target.value),
                                        }))
                                    }
                                    className={clsx(
                                        "form-select-field w-40",
                                        errors.fiscalStartMonth && "border-red-500",
                                    )}
                                >
                                    <option value="">未設定</option>
                                    {MONTH_OPTIONS.map((month) => (
                                        <option key={month} value={month}>
                                            {month}月
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            <FormField label="月の開始日" required error={errors.monthStartDay}>
                                <select
                                    value={form.monthStartDay ?? ""}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            monthStartDay: toNullableNumber(e.target.value),
                                        }))
                                    }
                                    className={clsx(
                                        "form-select-field w-40",
                                        errors.monthStartDay && "border-red-500",
                                    )}
                                >
                                    <option value="">未設定</option>
                                    {DAY_OPTIONS.map((day) => (
                                        <option key={day} value={day}>
                                            {day}日
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            <FormField label="曜日の始まり" required error={errors.weekStartDay}>
                                <select
                                    value={form.weekStartDay ?? ""}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            weekStartDay: toNullableNumber(e.target.value),
                                        }))
                                    }
                                    className={clsx(
                                        "form-select-field w-40",
                                        errors.weekStartDay && "border-red-500",
                                    )}
                                >
                                    <option value="">未設定</option>
                                    {WEEK_DAY_LABELS.map((label, index) => (
                                        <option key={index} value={index}>
                                            {label}曜日
                                        </option>
                                    ))}
                                </select>
                            </FormField>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        size="md"
                        className="px-8 bg-white"
                        onClick={handleCancel}
                        disabled={!isDirty || isSaving}
                    >
                        キャンセル
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        className="px-8"
                        onClick={handleSubmit}
                        disabled={!isDirty || hasError || isSaving}
                        isLoading={isSaving}
                    >
                        登録
                    </Button>
                </div>
            </div>

            <div
                className={clsx(
                    "fixed top-4 right-4 z-50 bg-white border border-gray-200 rounded-lg shadow-xl px-5 py-3 flex items-center gap-3 transition-all duration-300",
                    saveMessage
                        ? "translate-x-0 opacity-100"
                        : "translate-x-full opacity-0 pointer-events-none",
                )}
            >
                <div
                    className={clsx(
                        "w-2 h-2 rounded-full flex-shrink-0",
                        saveMessage?.type === "error" ? "bg-error-500" : "bg-brand-500",
                    )}
                />
                <span className="text-sm text-gray-700 font-medium">
                    {saveMessage?.text}
                </span>
            </div>
        </div>
    );
};

export default TenantBasicPage;
